import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, timeAgo } from "@/lib/api";
import { Avatar, Spinner } from "@/components/Common";

export function CommentsSheet({ contentId, open, onOpenChange, onCount }: any) {
  const [items, setItems] = useState<any[] | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) api.get(`/contents/${contentId}/comments`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [open, contentId]);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/contents/${contentId}/comments`, { text });
      setItems((x) => [data, ...(x || [])]);
      setText("");
      onCount?.(1);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  const del = async (id: string) => {
    await api.delete(`/comments/${id}`).catch(() => {});
    setItems((x) => (x || []).filter((c) => c.id !== id));
    onCount?.(-1);
  };
  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="menu-panel rounded-3xl max-h-[80dvh] md:max-w-xl md:mx-auto" data-testid="comments-sheet">
        <DrawerHeader><DrawerTitle className="font-display text-left">Commenti</DrawerTitle></DrawerHeader>
        <div className="overflow-y-auto px-4 space-y-4 min-h-[200px]">
          {items === null ? <Spinner /> : items.length === 0 ? <p className="text-ink/62 text-sm">Nessun commento. Scrivi il primo.</p> :
            items.map((c) => (
              <div key={c.id} className="flex gap-3" data-testid="comment-item">
                <Avatar user={c.user} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-ink/62"><Link to={`/u/${c.user?.username}`} className="text-foreground font-semibold hover:text-primary">@{c.user?.username}</Link> · {timeAgo(c.created_at)}</div>
                  <p className="text-sm break-words">{c.text}</p>
                </div>
                {c.is_mine && <button aria-label="Elimina commento" onClick={() => del(c.id)} className="text-ink/56 hover:text-primary" data-testid="comment-delete-btn"><Trash2 size={14} /></button>}
              </div>
            ))}
        </div>
        <form onSubmit={submit} className="flex gap-2 p-4 border-t border-ink/10">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={500} placeholder="Scrivi un commento…" className="field" data-testid="comment-input" />
          <button className="btn btn-primary px-4" disabled={busy || !text.trim()} aria-label="Invia" data-testid="comment-submit-btn"><Send size={16} /></button>
        </form>
      </DrawerContent>
    </Drawer>
  );
}

const REASONS: [string, string][] = [["spam", "Spam o truffa"], ["nudita", "Nudità o contenuti sessuali"], ["violenza", "Violenza"], ["odio", "Incitamento all'odio"],
  ["molestie", "Bullismo o molestie"], ["falso", "Informazioni false"], ["copyright", "Violazione di copyright"], ["minori", "Sicurezza dei minori"], ["altro", "Altro"]];

export function ReportDialog({ contentId, open, onOpenChange }: any) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const submit = async () => {
    try {
      await api.post(`/contents/${contentId}/report`, { reason, details });
      toast.success("Segnalazione inviata. Grazie, la valuteremo.");
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="menu-panel rounded-3xl" data-testid="report-dialog">
        <DialogHeader><DialogTitle className="font-display">Segnala contenuto</DialogTitle></DialogHeader>
        <div className="grid gap-2">
          {REASONS.map(([k, l]) => (
            <button key={k} onClick={() => setReason(k)} data-testid={`report-reason-${k}`} className={`text-left px-4 py-3 border transition-colors ${reason === k ? "border-primary text-primary" : "border-ink/10 hover:border-ink/20"}`}>{l}</button>
          ))}
          <textarea value={details} onChange={(e) => setDetails(e.target.value)} maxLength={1000} placeholder="Dettagli (facoltativo)" className="field min-h-[80px]" data-testid="report-details" />
          <button className="btn btn-primary" disabled={!reason} onClick={submit} data-testid="report-submit-btn">Invia segnalazione</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
